import React, { useEffect, useState } from 'react';
import { apiCall, useAuth } from '../AuthContext';
import { Loader2, CheckCircle2, Sparkles, AlertCircle, Store } from 'lucide-react';

/**
 * Plan picker shown after a merchant installs the app from the Shopify App Store.
 * App Store policy 1.2.1 requires Shopify Billing API for these users.
 * Direct customers (signed up at xlabelsync.com) never see this page — they go
 * through Stripe Checkout from the Landing page.
 */
export default function ShopifyPlanSelect() {
  const { user, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await apiCall('/api/shopify/billing/plans', { method: 'GET' });
        setPlans(r.data.plans || []);
        setTrialDays(r.data.trial_days_default ?? 7);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Show a friendly banner if user landed here via ?shopify_billing=success/declined
  const urlParams = new URLSearchParams(window.location.search);
  const billingOutcome = urlParams.get('shopify_billing');

  const subscribe = async (planId) => {
    setSubmitting(planId); setErr('');
    try {
      const r = await apiCall('/api/shopify/billing/create-subscription', {
        method: 'POST',
        data: { subscription_id: planId },
      });
      if (r.data.confirmation_url) {
        // Top-level navigate, NOT a popup. Shopify will return to /api/shopify/billing/return.
        window.top.location.href = r.data.confirmation_url;
      } else {
        setErr('Shopify did not return a confirmation URL.');
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubmitting(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0052FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="shopify-plan-select-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold mb-4">
            <Store className="w-3.5 h-3.5" /> Shopify App Store
          </div>
          <h1 className="text-4xl font-bold text-[#0F172A]">Pick a plan to get started</h1>
          <p className="text-base text-slate-600 mt-3">
            All plans come with a <strong>{trialDays}-day free trial</strong>. Billed through your
            Shopify invoice. Cancel anytime — no charge during trial.
          </p>
        </div>

        {billingOutcome === 'declined' && (
          <div className="max-w-2xl mx-auto mb-6 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="declined-banner">
            <AlertCircle className="w-4 h-4 inline -mt-0.5 mr-1" />
            You declined the subscription. Pick another plan to continue, or close this page to stay on the free trial.
          </div>
        )}
        {billingOutcome === 'pending' && (
          <div className="max-w-2xl mx-auto mb-6 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-sm">
            Your subscription is being processed. This usually takes a few seconds — refresh in a moment.
          </div>
        )}
        {err && (
          <div className="max-w-2xl mx-auto mb-6 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="plan-error">
            <AlertCircle className="w-4 h-4 inline -mt-0.5 mr-1" /> {err}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map(p => (
            <div key={p.subscription_id}
              className={`relative bg-white rounded-2xl p-6 border-2 shadow-sm transition-shadow hover:shadow-lg flex flex-col ${p.is_popular ? 'border-[#0052FF]' : 'border-slate-200'}`}
              data-testid={`plan-card-${p.subscription_id}`}>
              {p.is_popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#0052FF] text-white text-[10px] font-bold rounded-full inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> MOST POPULAR
                </div>
              )}
              <h3 className="text-lg font-bold text-[#0F172A]">{p.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{p.tagline}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-[#0F172A]">${p.monthly_price}</span>
                <span className="text-sm text-slate-500">/mo</span>
              </div>
              <p className="text-[11px] text-emerald-700 font-semibold mt-1">{p.trial_days}-day free trial</p>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="text-sm text-slate-700 inline-flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => subscribe(p.subscription_id)}
                disabled={!!submitting}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 ${p.is_popular ? 'bg-[#0052FF] hover:bg-[#0040CC] text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                data-testid={`plan-subscribe-${p.subscription_id}`}>
                {submitting === p.subscription_id ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Shopify…</>
                ) : (
                  <>Start {p.trial_days}-day free trial</>
                )}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-slate-400 mt-10">
          By subscribing you agree to be billed through your Shopify invoice. You can cancel anytime from Shopify Admin → Apps.
        </p>
      </div>
    </div>
  );
}
